import { useTranslation } from 'react-i18next';
import {
  Search, ChevronDown, ChevronUp, ArrowRight, ExternalLink,
  FileText, Shield, Truck, BookOpen, CheckCircle, Users,
  Star, Grid3x3, Sparkles, Percent, Zap, HelpCircle, AlertTriangle
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { categories, heroImages, articleList } from '@/mocks/data';
import { productCategories } from '@/mocks/products';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function Home() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.category-dropdown')) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const valueCards = [
    { icon: FileText, title: t('value_spreadsheet'), desc: t('value_spreadsheet_desc'), color: 'bg-primary-50 text-primary-700' },
    { icon: Grid3x3, title: t('value_discovery'), desc: t('value_discovery_desc'), color: 'bg-secondary-50 text-secondary-700' },
    { icon: Star, title: t('value_qc'), desc: t('value_qc_desc'), color: 'bg-accent-50 text-accent-700' },
    { icon: Truck, title: t('value_shipping'), desc: t('value_shipping_desc'), color: 'bg-primary-50 text-primary-700' },
    { icon: Shield, title: t('value_safety'), desc: t('value_safety_desc'), color: 'bg-secondary-50 text-secondary-700' },
    { icon: BookOpen, title: t('value_comparison'), desc: t('value_comparison_desc'), color: 'bg-accent-50 text-accent-700' },
  ];

  const steps = [
    { icon: Grid3x3, title: t('how_step1_title'), desc: t('how_step1_desc') },
    { icon: FileText, title: t('how_step2_title'), desc: t('how_step2_desc') },
    { icon: CheckCircle, title: t('how_step3_title'), desc: t('how_step3_desc') },
    { icon: ArrowRight, title: t('how_step4_title'), desc: t('how_step4_desc') },
  ];

  const trustPoints = [
    t('trust_point1'), t('trust_point2'), t('trust_point3'), t('trust_point4'), t('trust_point5'),
  ];

  const comparisonData = [
    { platform: 'AllChinaBuy', spreadsheet: true, qc: true, shipping: true, beginner: true },
    { platform: 'Superbuy', spreadsheet: true, qc: true, shipping: true, beginner: true },
    { platform: 'CSSBuy', spreadsheet: false, qc: true, shipping: true, beginner: false },
    { platform: 'Sugargoo', spreadsheet: true, qc: true, shipping: true, beginner: true },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[85vh] md:min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={heroImages[0]}
            alt="AllChinaBuy Spreadsheet fashion products"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 pt-24 pb-16">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-background-50/10 backdrop-blur-sm border border-background-50/20 text-background-50 text-xs font-semibold uppercase tracking-wider mb-6">
              {t('hero_eyebrow')}
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight mb-6 text-balance">
              {t('hero_title')}
            </h1>
            <p className="text-base md:text-lg text-background-200/90 max-w-2xl mx-auto mb-8 leading-relaxed">
              {t('hero_subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-8 py-3.5 text-sm font-semibold text-foreground-950 bg-background-50 hover:bg-background-100 rounded-lg transition-colors flex items-center gap-2"
              >
                {t('hero_cta_primary')}
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="#categories"
                className="px-8 py-3.5 text-sm font-semibold text-background-50 border border-background-50/40 hover:bg-background-50/10 rounded-lg transition-colors"
              >
                {t('hero_cta_secondary')}
              </a>
            </div>
            <p className="text-sm text-background-300/80 flex items-center justify-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
              {t('hero_trust')}
            </p>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-6 h-6 text-background-50/60" />
        </div>
      </section>

      {/* Category Dropdown Section */}
      <section id="categories" className="py-16 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              {t('categories_title')}
            </h2>
            <p className="text-foreground-600 text-base">
              {t('categories_description')}
            </p>
          </div>

          {/* Dropdown */}
          <div className="max-w-2xl mx-auto category-dropdown">
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpen(!dropdownOpen);
                }}
                className="w-full flex items-center justify-between px-5 py-4 bg-background-50 border-2 border-background-200 hover:border-primary-300 rounded-xl text-left transition-colors shadow-sm"
              >
                <span className="font-semibold text-foreground-800">
                  {t('categories_button')}
                </span>
                {dropdownOpen ? (
                  <ChevronUp className="w-5 h-5 text-foreground-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-foreground-500" />
                )}
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 rounded-xl border border-background-200/70 shadow-lg overflow-hidden z-20 animate-in fade-in slide-in-from-top-1 duration-150">
                  <div className="max-h-[400px] overflow-y-auto p-2">
                    {categories.map((cat, idx) => (
                      <a
                        key={idx}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center justify-between px-4 py-3 rounded-lg text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors group"
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center text-xs font-bold">
                            {cat.name.charAt(0)}
                          </span>
                          <div>
                            <p className="font-medium">{cat.name}</p>
                            <p className="text-xs text-foreground-500">{cat.description}</p>
                          </div>
                        </div>
                        <ExternalLink className="w-4 h-4 text-foreground-400 group-hover:text-foreground-600 transition-colors" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main CTAs Section */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-8 py-4 text-sm font-bold text-background-50 bg-primary-600 hover:bg-primary-700 rounded-xl transition-colors flex items-center justify-center gap-2 shadow-lg shadow-primary-600/20"
              >
                <Sparkles className="w-5 h-5" />
                {t('cta_spreadsheet')}
                <ExternalLink className="w-4 h-4" />
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-8 py-4 text-sm font-bold text-foreground-950 bg-background-50 hover:bg-background-200 border-2 border-foreground-200 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <Zap className="w-5 h-5" />
                {t('cta_buy_now')}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-2">
              {t('search_title')}
            </h2>
            <p className="text-foreground-500 text-center text-sm mb-6">
              {t('search_suggested')}: AllChinaBuy spreadsheet, Shoes, Fashion, QC guide, Shipping, Warehouse, Coupons
            </p>
            <div className={`relative transition-all duration-300 ${searchFocused ? 'scale-[1.02]' : ''}`}>
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground-400" />
              <input
                type="text"
                placeholder={t('search_placeholder')}
                className="w-full pl-12 pr-4 py-4 bg-background-50 border-2 border-background-200 hover:border-primary-300 focus:border-primary-500 rounded-xl text-sm text-foreground-800 placeholder:text-foreground-400 outline-none transition-colors shadow-sm"
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-3">
              {t('value_title')}
            </h2>
            <p className="text-foreground-600 text-center text-sm md:text-base max-w-2xl mx-auto mb-12">
              Independent resources, practical guides, and safety-focused information to help you navigate AllChinaBuy with confidence.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {valueCards.map((card, idx) => (
                <div
                  key={idx}
                  className="group p-6 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300/50 transition-all hover:shadow-md"
                >
                  <div className={`w-11 h-11 rounded-lg ${card.color} flex items-center justify-center mb-4`}>
                    <card.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-semibold text-foreground-900 mb-2">{card.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Spreadsheet */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="flex-1">
                <span className="inline-block px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold uppercase tracking-wider mb-4">
                  Featured Resource
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  {t('featured_spreadsheet')}
                </h2>
                <p className="text-foreground-600 leading-relaxed mb-6">
                  {t('featured_spreadsheet_desc')}
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-background-50 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                >
                  {t('explore_spreadsheet')}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
              <div className="flex-1 w-full">
                <div className="relative rounded-xl overflow-hidden shadow-lg">
                  <img
                    src={heroImages[1]}
                    alt="AllChinaBuy spreadsheet product categories"
                    className="w-full h-64 md:h-80 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Categories */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-3">
              {t('popular_title')}
            </h2>
            <p className="text-foreground-600 text-center text-sm md:text-base max-w-2xl mx-auto mb-12">
              {t('popular_desc')}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {categories.map((cat, idx) => (
                <a
                  key={idx}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group p-5 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300/50 hover:shadow-md transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center text-sm font-bold mb-3 group-hover:bg-primary-100 transition-colors">
                    {cat.name.charAt(0)}
                  </div>
                  <h3 className="font-semibold text-foreground-900 text-sm mb-1 group-hover:text-primary-700 transition-colors">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{cat.description}</p>
                  <div className="mt-3 flex items-center gap-1 text-xs text-primary-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    Explore
                    <ExternalLink className="w-3 h-3" />
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Products by Category */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                {t('products_title')}
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
                {t('products_subtitle')}
              </p>
            </div>

            {productCategories.map((group, gIdx) => (
              <div key={gIdx} className="mb-14 last:mb-0">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg md:text-xl font-bold text-foreground-900">
                    {group.name}
                  </h3>
                  <a
                    href={group.categoryUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm font-medium text-primary-600 hover:text-primary-800 flex items-center gap-1 transition-colors"
                  >
                    {t('products_view_all', { category: group.name })}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                  {group.products.map((product) => (
                    <a
                      key={product.id}
                      href={product.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="group block bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300/50 hover:shadow-md transition-all overflow-hidden"
                    >
                      <div className="aspect-square overflow-hidden bg-background-100">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-3 md:p-4">
                        <h4 className="text-sm font-medium text-foreground-900 leading-snug mb-2 line-clamp-2 group-hover:text-primary-700 transition-colors">
                          {product.name}
                        </h4>
                        <div className="flex items-center justify-between">
                          <div className="flex items-baseline gap-1.5">
                            <span className="text-sm font-bold text-primary-700">{product.priceCny}</span>
                            <span className="text-xs text-foreground-500">{product.priceUsd}</span>
                          </div>
                          <span className="text-xs font-semibold text-background-50 bg-primary-600 hover:bg-primary-700 px-2.5 py-1 rounded-md transition-colors">
                            {t('products_buy_now')}
                          </span>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-12">
              {t('how_title')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step, idx) => (
                <div key={idx} className="relative text-center">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-primary-50 text-primary-600 flex items-center justify-center mb-4">
                    <step.icon className="w-6 h-6" />
                  </div>
                  <div className="w-8 h-8 mx-auto -mt-10 mb-4 rounded-full bg-primary-600 text-background-50 text-xs font-bold flex items-center justify-center">
                    {idx + 1}
                  </div>
                  <h3 className="font-semibold text-foreground-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                  {idx < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-7 left-[calc(100%+12px)] w-[calc(100%-24px)] h-px bg-background-200" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-secondary-50 text-secondary-600 flex items-center justify-center mb-6">
              <Shield className="w-6 h-6" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              {t('trust_title')}
            </h2>
            <p className="text-foreground-600 leading-relaxed max-w-2xl mx-auto mb-10">
              {t('trust_desc')}
            </p>
            <div className="flex flex-wrap justify-center gap-3 md:gap-4">
              {trustPoints.map((point, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-background-50 rounded-full text-sm text-foreground-700 border border-background-200/70"
                >
                  <CheckCircle className="w-4 h-4 text-secondary-500" />
                  {point}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-3">
              {t('comparison_title')}
            </h2>
            <p className="text-foreground-500 text-center text-sm mb-10">
              {t('comparison_note')}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-background-200">
                    <th className="text-left py-3 px-4 font-semibold text-foreground-800">{t('comparison_platform')}</th>
                    <th className="text-center py-3 px-4 font-semibold text-foreground-800">{t('comparison_spreadsheet')}</th>
                    <th className="text-center py-3 px-4 font-semibold text-foreground-800">{t('comparison_qc')}</th>
                    <th className="text-center py-3 px-4 font-semibold text-foreground-800">{t('comparison_shipping_info')}</th>
                    <th className="text-center py-3 px-4 font-semibold text-foreground-800">{t('comparison_beginner')}</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, idx) => (
                    <tr key={idx} className="border-b border-background-200/70 hover:bg-background-100/50 transition-colors">
                      <td className="py-3 px-4 font-medium text-foreground-900">{row.platform}</td>
                      <td className="text-center py-3 px-4">
                        {row.spreadsheet ? (
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <span className="text-foreground-400">—</span>
                        )}
                      </td>
                      <td className="text-center py-3 px-4">
                        {row.qc ? (
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <span className="text-foreground-400">—</span>
                        )}
                      </td>
                      <td className="text-center py-3 px-4">
                        {row.shipping ? (
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <span className="text-foreground-400">—</span>
                        )}
                      </td>
                      <td className="text-center py-3 px-4">
                        {row.beginner ? (
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <span className="text-foreground-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Safety Section */}
      <section className="py-16 md:py-20 bg-secondary-50/50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
              <div className="flex-1">
                <div className="w-14 h-14 rounded-2xl bg-secondary-100 text-secondary-700 flex items-center justify-center mb-5">
                  <AlertTriangle className="w-6 h-6" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  {t('safety_title')}
                </h2>
                <p className="text-foreground-600 leading-relaxed mb-6">
                  {t('safety_desc')}
                </p>
                <a
                  href="/safety"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-background-50 bg-secondary-600 hover:bg-secondary-700 rounded-lg transition-colors"
                >
                  {t('safety_cta')}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
              <div className="flex-1 w-full p-6 bg-background-50 rounded-xl border border-background-200/70">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
                    <HelpCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground-900 mb-1">Safety Notice</h4>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {t('safety_notice')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-3">
              {t('articles_title')}
            </h2>
            <p className="text-foreground-600 text-center text-sm md:text-base max-w-2xl mx-auto mb-12">
              {t('articles_desc')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {articleList.map((article, idx) => (
                <a
                  key={idx}
                  href={`/article/${article.slug}`}
                  className="group p-6 bg-background-100 rounded-xl border border-background-200/70 hover:border-primary-300/50 hover:shadow-md transition-all"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-2.5 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold">
                      {article.category}
                    </span>
                    <span className="text-xs text-foreground-500">{article.date}</span>
                    <span className="text-xs text-foreground-500">{article.readTime}</span>
                  </div>
                  <h3 className="font-semibold text-foreground-900 mb-2 group-hover:text-primary-700 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    {article.excerpt}
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 group-hover:text-primary-700 transition-colors">
                    {t('articles_read_more')}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-24 bg-foreground-950 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url(${heroImages[0]})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }} />
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-background-50 mb-4">
              {t('final_cta_title')}
            </h2>
            <p className="text-foreground-400 leading-relaxed mb-8 max-w-xl mx-auto">
              {t('final_cta_desc')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-8 py-3.5 text-sm font-semibold text-foreground-950 bg-background-50 hover:bg-background-100 rounded-lg transition-colors flex items-center gap-2"
              >
                {t('final_cta_primary')}
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="/guides"
                className="px-8 py-3.5 text-sm font-semibold text-background-50 border border-foreground-700 hover:border-foreground-500 rounded-lg transition-colors"
              >
                {t('final_cta_secondary')}
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}